import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Edit2, Eye, Loader2, Search, Upload, X, Download, FileSpreadsheet, Image as ImageIcon, Calendar as CalendarIcon, Building2, Phone, User, ClipboardList, StickyNote, Camera, Wrench } from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { OrangeCheckbox } from '../shared/WizardComponents';
import { maintenanceListConfig, maintenanceFormFields, formatDateUS } from './maintenanceConfig';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { cn } from '../../lib/utils';

// ── Photo Upload Area ──
const PhotoUploadArea = ({ photos, pendingFiles, uploading, onUpload, onRemove, onRemovePending }) => {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (files) => {
    const images = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (images.length === 0) { toast.error('Only image files are allowed'); return; }
    images.forEach(f => onUpload(f));
  };

  const allPhotos = [
    ...(photos || []).map(p => ({ ...p, source: 'server' })),
    ...(pendingFiles || []).map(p => ({ ...p, source: 'pending' })),
  ];

  return (
    <div className="space-y-2">
      <Label className="text-sm">Photos</Label>
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${dragOver ? 'border-orange-400 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        data-testid="photo-upload-area"
      >
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
        <Camera className="h-8 w-8 mx-auto text-slate-300 mb-2" />
        <p className="text-sm text-slate-500">Drag & drop photos here or click to browse</p>
        {uploading && <Loader2 className="h-4 w-4 animate-spin mx-auto mt-2 text-orange-500" />}
      </div>
      {allPhotos.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {allPhotos.map((p) => (
            <div key={p.id} className="relative group w-20 h-20 rounded-lg overflow-hidden border" data-testid={`photo-thumb-${p.id}`}>
              <img src={p.source === 'pending' ? p.previewUrl : (p.thumbnail || p.url)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => { e.stopPropagation(); p.source === 'pending' ? onRemovePending(p.id) : onRemove(p.id); }}
                data-testid={`photo-remove-${p.id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Technician Combobox ──
const TechnicianSelect = ({ value, onChange, technicians, error, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = technicians.filter(t => t.toLowerCase().includes(search.toLowerCase()));
  const showAddNew = search.trim() && !technicians.some(t => t.toLowerCase() === search.trim().toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={`flex items-center border rounded-md px-3 py-2 cursor-pointer ${error ? 'border-red-500' : 'border-input'}`} data-testid="technician-select">
          {value ? <span className="text-sm">{value}</span> : <span className="text-muted-foreground text-sm">{placeholder}</span>}
          {value && <X className="h-4 w-4 ml-auto text-slate-400 hover:text-slate-600" onClick={(e) => { e.stopPropagation(); onChange(''); }} />}
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] max-h-48 overflow-y-auto" align="start" sideOffset={4}>
        <div className="sticky top-0 bg-white p-2 border-b">
          <Input className="h-8 text-sm" placeholder="Search or type new..." value={search} onChange={(e) => setSearch(e.target.value)} autoFocus data-testid="technician-search" />
        </div>
        {filtered.map(t => (
          <div key={t} className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm" onClick={() => { onChange(t); setSearch(''); setOpen(false); }} data-testid={`technician-opt-${t}`}>{t}</div>
        ))}
        {showAddNew && (
          <div className="px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm text-orange-600 font-medium border-t" onClick={() => { onChange(search.trim()); setSearch(''); setOpen(false); }} data-testid="technician-add-new">
            <Plus className="h-3.5 w-3.5 inline mr-1" />Add: "{search.trim()}"
          </div>
        )}
        {filtered.length === 0 && !showAddNew && <div className="px-3 py-2 text-sm text-slate-400">No technicians found</div>}
      </PopoverContent>
    </Popover>
  );
};

// ── Review Modal ──
const MaintenanceReviewModal = ({ open, onOpenChange, item }) => {
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  if (!item) return null;

  const TARGET_TYPE_LABELS = { equipment: 'Equipment', device: 'Device', panel: 'Panel' };
  const targetDisplay = item.targetType
    ? `${TARGET_TYPE_LABELS[item.targetType] || item.targetType}${item.targetName ? ': ' + item.targetName : ''}`
    : '-';

  const fields = [
    { label: 'Date', value: formatDateUS(item.date), icon: CalendarIcon },
    { label: 'Maintenance For', value: targetDisplay, icon: Wrench },
    { label: 'Company Name', value: item.companyName || '-', icon: Building2 },
    { label: 'Company Contact', value: item.companyContact || '-', icon: Phone },
    { label: 'Technician', value: item.technician || '-', icon: User },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="maint-review-modal">
          <DialogHeader>
            <DialogTitle>Maintenance Details</DialogTitle>
            <DialogDescription>Review the maintenance record information below.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 py-2">
            {fields.map(f => (
              <div key={f.label} data-testid={`review-${f.label.toLowerCase().replace(/\s/g, '-')}`}>
                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                  <f.icon className="h-3 w-3" />{f.label}
                </dt>
                <dd className="text-sm text-slate-900">{f.value}</dd>
              </div>
            ))}
          </div>
          <div className="pt-2 border-t" data-testid="review-action">
            <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1"><ClipboardList className="h-3 w-3" />Action</dt>
            <dd className="text-sm text-slate-900 whitespace-pre-wrap">{item.action || '-'}</dd>
          </div>
          {item.photos?.length > 0 && (
            <div className="pt-2 border-t" data-testid="review-photos">
              <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1"><ImageIcon className="h-3 w-3" />Photos ({item.photos.length})</dt>
              <div className="flex flex-wrap gap-3">
                {item.photos.map(p => (
                  <button key={p.id} type="button" className="w-24 h-24 rounded-lg overflow-hidden border hover:ring-2 ring-orange-400 transition-all cursor-pointer" onClick={() => setEnlargedPhoto(p.url)} data-testid={`review-photo-${p.id}`}>
                    <img src={p.thumbnail || p.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {item.notes && (
            <div className="pt-2 border-t" data-testid="review-notes">
              <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1"><StickyNote className="h-3 w-3" />Notes</dt>
              <dd className="text-sm text-slate-900 whitespace-pre-wrap">{item.notes}</dd>
            </div>
          )}
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="maint-review-close">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Enlarged Photo Modal */}
      <Dialog open={!!enlargedPhoto} onOpenChange={() => setEnlargedPhoto(null)}>
        <DialogContent className="max-w-4xl p-2" data-testid="photo-enlarge-modal">
          <DialogHeader><DialogTitle className="sr-only">Photo</DialogTitle></DialogHeader>
          {enlargedPhoto && <img src={enlargedPhoto} alt="" className="w-full h-auto max-h-[80vh] object-contain rounded" />}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ── Create/Edit Modal ──
const MaintenanceFormModal = ({ open, onOpenChange, editItem, onSave, onRefresh, technicians, projectId, csiItemId }) => {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Target entity state
  const [targetEntities, setTargetEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setForm({
        date: editItem.date || '',
        companyName: editItem.companyName || '',
        companyContact: editItem.companyContact || '',
        technician: editItem.technician || '',
        action: editItem.action || '',
        notes: editItem.notes || '',
        targetType: editItem.targetType || '',
        targetId: editItem.targetId || '',
      });
      setPhotos(editItem.photos || []);
      // Fetch target entities if type is set
      if (editItem.targetType) {
        fetchTargetEntities(editItem.targetType);
      }
    } else {
      setForm({ date: '', companyName: '', companyContact: '', technician: '', action: '', notes: '', targetType: '', targetId: '' });
      setPhotos([]);
      setTargetEntities([]);
    }
    // Revoke old pending preview URLs
    setPendingFiles(prev => { prev.forEach(p => URL.revokeObjectURL(p.previewUrl)); return []; });
    setErrors({});
  }, [open, editItem]);

  const fetchTargetEntities = async (type) => {
    if (!type || !projectId) { setTargetEntities([]); return; }
    setLoadingEntities(true);
    try {
      const endpointMap = {
        equipment: `/electrical-systems/${projectId}/equipment-list`,
        device: `/electrical-systems/${projectId}/device-list`,
        panel: `/electrical-systems/${projectId}/panel-list`,
      };
      const res = await axios.get(`${API}${endpointMap[type]}`);
      setTargetEntities(res.data || []);
    } catch {
      setTargetEntities([]);
    } finally {
      setLoadingEntities(false);
    }
  };

  const handleTargetTypeChange = (val) => {
    const newType = val === 'none' ? '' : val;
    setForm(prev => ({ ...prev, targetType: newType, targetId: '' }));
    if (errors.targetType) setErrors(prev => ({ ...prev, targetType: null }));
    if (errors.targetId) setErrors(prev => ({ ...prev, targetId: null }));
    fetchTargetEntities(newType);
  };

  const getEntityLabel = (entity) => {
    return entity.tag || entity.deviceTag || entity.name || entity.id;
  };

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  const validate = () => {
    const errs = {};
    maintenanceFormFields.forEach(f => {
      if (f.required && !form[f.key]?.trim()) errs[f.key] = `${f.label} is required`;
    });
    if (!form.targetType) errs.targetType = 'Maintenance For is required';
    if (form.targetType && !form.targetId) errs.targetId = 'Please select a target';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handlePhotoUpload = (file) => {
    if (editItem) {
      // Edit mode: upload directly to server
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      axios.post(`${API}/electrical-maintenance/${projectId}/${editItem.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(res => setPhotos(prev => [...prev, res.data]))
        .catch(() => toast.error('Failed to upload photo'))
        .finally(() => setUploading(false));
    } else {
      // Create mode: store locally as pending
      const previewUrl = URL.createObjectURL(file);
      setPendingFiles(prev => [...prev, { id: `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`, file, previewUrl }]);
    }
  };

  const handlePhotoRemove = async (photoId) => {
    if (!editItem) return;
    try {
      await axios.delete(`${API}/electrical-maintenance/${projectId}/${editItem.id}/photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch {
      toast.error('Failed to remove photo');
    }
  };

  const handlePendingRemove = (pendingId) => {
    setPendingFiles(prev => {
      const removed = prev.find(p => p.id === pendingId);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter(p => p.id !== pendingId);
    });
  };

  const uploadPendingPhotos = async (recordId) => {
    for (const pf of pendingFiles) {
      try {
        const fd = new FormData();
        fd.append('file', pf.file);
        await axios.post(`${API}/electrical-maintenance/${projectId}/${recordId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch {
        // Continue uploading remaining photos
      }
      URL.revokeObjectURL(pf.previewUrl);
    }
    setPendingFiles([]);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form, csiItemId };
      // Send empty strings as null for targetType/targetId
      if (!payload.targetType) { payload.targetType = ''; payload.targetId = ''; }
      const result = await onSave(payload, editItem);
      // If creating and we have pending photos, upload them now
      if (!editItem && pendingFiles.length > 0 && result?.id) {
        await uploadPendingPhotos(result.id);
        onRefresh?.();
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save maintenance record');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="maint-form-modal">
        <DialogHeader>
          <DialogTitle>{editItem ? 'Update Maintenance' : 'Add Maintenance Record'}</DialogTitle>
          <DialogDescription>{editItem ? 'Update the maintenance record details below.' : 'Fill in the details to add a new maintenance record.'}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-1 space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            {/* Date */}
            <div className="space-y-1" data-testid="field-date">
              <Label className="text-sm">Date *</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal h-9",
                      !form.date && "text-muted-foreground",
                      errors.date && "border-red-500"
                    )}
                    data-testid="input-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.date ? formatDateUS(form.date) : 'Select date...'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.date ? new Date(form.date + 'T00:00:00') : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, '0');
                        const d = String(date.getDate()).padStart(2, '0');
                        handleChange('date', `${y}-${m}-${d}`);
                      } else {
                        handleChange('date', '');
                      }
                      setCalendarOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {errors.date && <p className="text-xs text-red-500">{errors.date}</p>}
            </div>
            {/* Company Name */}
            <div className="space-y-1" data-testid="field-companyName">
              <Label className="text-sm">Company Name *</Label>
              <Input value={form.companyName || ''} onChange={(e) => handleChange('companyName', e.target.value)} placeholder="Enter company name" className={errors.companyName ? 'border-red-500' : ''} data-testid="input-companyName" />
              {errors.companyName && <p className="text-xs text-red-500">{errors.companyName}</p>}
            </div>
            {/* Company Contact */}
            <div className="space-y-1" data-testid="field-companyContact">
              <Label className="text-sm">Company Contact</Label>
              <Input type="tel" value={form.companyContact || ''} onChange={(e) => handleChange('companyContact', e.target.value)} placeholder="+1 555-0100" data-testid="input-companyContact" />
            </div>
            {/* Technician */}
            <div className="space-y-1" data-testid="field-technician">
              <Label className="text-sm">Technician *</Label>
              <TechnicianSelect value={form.technician || ''} onChange={(v) => handleChange('technician', v)} technicians={technicians} error={errors.technician} placeholder="Select or type technician name" />
              {errors.technician && <p className="text-xs text-red-500">{errors.technician}</p>}
            </div>
          </div>
          {/* Maintenance Target */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-4" data-testid="field-target">
            <div className="space-y-1">
              <Label className="text-sm">Maintenance For *</Label>
              <Select value={form.targetType || 'none'} onValueChange={handleTargetTypeChange} modal={false}>
                <SelectTrigger className={`h-9 ${errors.targetType ? 'border-red-500' : ''}`} data-testid="select-target-type">
                  <SelectValue placeholder="Select target type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select...</SelectItem>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="device">Device</SelectItem>
                  <SelectItem value="panel">Panel</SelectItem>
                </SelectContent>
              </Select>
              {errors.targetType && <p className="text-xs text-red-500">{errors.targetType}</p>}
            </div>
            {form.targetType && (
              <div className="space-y-1">
                <Label className="text-sm">
                  {form.targetType === 'equipment' ? 'Equipment Tag *' : form.targetType === 'device' ? 'Device Tag *' : 'Panel Name *'}
                </Label>
                {loadingEntities ? (
                  <div className="h-9 flex items-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
                ) : (
                  <Select value={form.targetId || 'none'} onValueChange={(v) => handleChange('targetId', v === 'none' ? '' : v)} modal={false}>
                    <SelectTrigger className={`h-9 ${errors.targetId ? 'border-red-500' : ''}`} data-testid="select-target-entity">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select...</SelectItem>
                      {targetEntities.map(e => (
                        <SelectItem key={e.id} value={e.id}>{getEntityLabel(e)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {errors.targetId && <p className="text-xs text-red-500">{errors.targetId}</p>}
              </div>
            )}
          </div>
          {/* Action */}
          <div className="space-y-1" data-testid="field-action">
            <Label className="text-sm">Action *</Label>
            <Textarea value={form.action || ''} onChange={(e) => handleChange('action', e.target.value)} placeholder="Describe the maintenance performed" rows={3} className={errors.action ? 'border-red-500' : ''} data-testid="textarea-action" />
            {errors.action && <p className="text-xs text-red-500">{errors.action}</p>}
          </div>
          {/* Photos - always visible */}
          <PhotoUploadArea
            photos={photos}
            pendingFiles={pendingFiles}
            uploading={uploading}
            onUpload={handlePhotoUpload}
            onRemove={handlePhotoRemove}
            onRemovePending={handlePendingRemove}
          />
          {/* Notes */}
          <div className="space-y-1" data-testid="field-notes">
            <Label className="text-sm">Notes</Label>
            <Textarea value={form.notes || ''} onChange={(e) => handleChange('notes', e.target.value)} placeholder="Additional notes..." rows={2} data-testid="textarea-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t shrink-0">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="maint-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid="maint-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {editItem ? 'Update Maintenance' : 'Save Maintenance'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ══════════════════════════════════════════
// Main Section
// ══════════════════════════════════════════
export const MaintenanceSection = ({ projectId, csiItem, canEditCSIItems, canManageProject, onItemCount }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [technicians, setTechnicians] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [reviewingRecord, setReviewingRecord] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterTechnician, setFilterTechnician] = useState('');

  // Exclusive dropdown behavior
  const [openDropdown, setOpenDropdown] = useState(null);
  const makeDropdownProps = (key) => ({
    open: openDropdown === key,
    onOpenChange: (open) => setOpenDropdown(prev => open ? key : (prev === key ? null : prev)),
    modal: false,
  });
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);

  const csiItemId = csiItem?.id;
  const config = maintenanceListConfig;

  useEffect(() => {
    if (onItemCount) onItemCount(records.length);
  }, [records.length, onItemCount]);

  const fetchRecords = useCallback(async () => {
    if (!projectId) return;
    try {
      const params = csiItemId ? `?csi_item_id=${csiItemId}` : '';
      const res = await axios.get(`${API}${config.apiBase}/${projectId}${params}`);
      setRecords(res.data);
    } catch {
      toast.error('Failed to load maintenance records');
    } finally {
      setLoading(false);
    }
  }, [projectId, csiItemId, config.apiBase]);

  const fetchTechnicians = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}${config.apiBase}/${projectId}/technicians`);
      setTechnicians(res.data);
    } catch {}
  }, [projectId, config.apiBase]);

  useEffect(() => { fetchRecords(); fetchTechnicians(); }, [fetchRecords, fetchTechnicians]);
  useEffect(() => { setSelectedIds(new Set()); }, [records]);

  // Filtering
  const filtered = records.filter(r => {
    if (filterDate && r.date !== filterDate) return false;
    if (filterCompany && r.companyName !== filterCompany) return false;
    if (filterTechnician && r.technician !== filterTechnician) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return config.columns.some(col => {
        const val = col.render ? col.render(r) : r[col.key];
        return val && String(val).toLowerCase().includes(term);
      });
    }
    return true;
  });

  const uniqueCompanies = [...new Set(records.map(r => r.companyName).filter(Boolean))].sort();
  const uniqueDates = [...new Set(records.map(r => r.date).filter(Boolean))].sort().reverse();

  const handleSave = async (formData, existingItem) => {
    if (existingItem) {
      await axios.put(`${API}${config.apiBase}/${projectId}/${existingItem.id}`, formData);
      toast.success('Maintenance record updated');
      fetchRecords();
      fetchTechnicians();
      return existingItem;
    } else {
      const res = await axios.post(`${API}${config.apiBase}/${projectId}`, formData);
      toast.success('Maintenance record created');
      fetchRecords();
      fetchTechnicians();
      return res.data;
    }
  };

  const handleDelete = (item) => {
    setConfirmState({
      open: true,
      title: 'Delete Maintenance Record',
      description: `Are you sure you want to delete this maintenance record from ${item.companyName}? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: async () => {
        setDeleting(item.id);
        try {
          await axios.delete(`${API}${config.apiBase}/${projectId}/${item.id}`);
          setRecords(prev => prev.filter(r => r.id !== item.id));
          toast.success('Maintenance record deleted');
        } catch {
          toast.error('Failed to delete record');
        } finally { setDeleting(null); }
        setConfirmState({ open: false });
      },
    });
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setConfirmState({
      open: true,
      title: 'Delete selected maintenance records?',
      description: `You are about to delete ${selectedIds.size} record(s). This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: async () => {
        setBulkDeleting(true);
        try {
          const res = await axios.post(`${API}${config.apiBase}/${projectId}/bulk-delete`, { ids: Array.from(selectedIds) });
          setRecords(prev => prev.filter(r => !selectedIds.has(r.id)));
          setSelectedIds(new Set());
          toast.success(`Deleted ${res.data.deleted} record(s)`);
        } catch { toast.error('Failed to delete records'); }
        finally { setBulkDeleting(false); }
        setConfirmState({ open: false });
      },
    });
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const params = csiItemId ? `?csi_item_id=${csiItemId}` : '';
      const res = await axios.post(`${API}${config.apiBase}/${projectId}/import-excel${params}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${res.data.imported} record(s)`);
      fetchRecords();
      fetchTechnicians();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import Excel');
    } finally { setImporting(false); }
  };

  const handleExport = async () => {
    try {
      const params = csiItemId ? `?csi_item_id=${csiItemId}` : '';
      const res = await axios.get(`${API}${config.apiBase}/${projectId}/export-excel${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'maintenance_records.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to export Excel'); }
  };

  const handleSampleDownload = async () => {
    try {
      const res = await axios.get(`${API}${config.apiBase}/${projectId}/sample-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'maintenance_sample.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download sample'); }
  };

  const toggleSelectAll = () => { selectedIds.size === filtered.length ? setSelectedIds(new Set()) : setSelectedIds(new Set(filtered.map(r => r.id))); };
  const toggleSelectRow = (id) => { setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < filtered.length;

  if (loading) {
    return <div className="flex justify-center py-16" data-testid="elec-maint-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div data-testid="elec-maint-section">
      {/* Search + Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Search records..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8" data-testid="elec-maint-search" />
        </div>
        <div className="w-[150px]">
          <Select value={filterDate || 'all'} onValueChange={(v) => setFilterDate(v === 'all' ? '' : v)} {...makeDropdownProps('date')}>
            <SelectTrigger className="h-9 text-sm" data-testid="filter-date"><SelectValue placeholder="All Dates" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Dates</SelectItem>
              {uniqueDates.map(d => <SelectItem key={d} value={d}>{new Date(d + 'T00:00:00').toLocaleDateString('en-US')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[170px]">
          <Select value={filterCompany || 'all'} onValueChange={(v) => setFilterCompany(v === 'all' ? '' : v)} {...makeDropdownProps('company')}>
            <SelectTrigger className="h-9 text-sm" data-testid="filter-company"><SelectValue placeholder="All Companies" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {uniqueCompanies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[170px]">
          <Select value={filterTechnician || 'all'} onValueChange={(v) => setFilterTechnician(v === 'all' ? '' : v)} {...makeDropdownProps('technician')}>
            <SelectTrigger className="h-9 text-sm" data-testid="filter-technician"><SelectValue placeholder="All Technicians" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Technicians</SelectItem>
              {technicians.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex justify-end items-center mb-4">
        <div className="flex items-center gap-2">
          {canManageProject && (
            <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={bulkDeleting || selectedIds.size === 0} data-testid="elec-maint-bulk-delete">
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Delete All{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleSampleDownload} data-testid="elec-maint-sample-btn">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" />Sample Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => importRef.current?.click()} disabled={importing} data-testid="elec-maint-import-btn">
            {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Import Excel
          </Button>
          <input ref={importRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          {canEditCSIItems && (
            <Button size="sm" onClick={() => { setEditingRecord(null); setShowModal(true); }} data-testid="elec-maint-create-btn">
              <Plus className="h-4 w-4 mr-1.5" />Add Maintenance
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg" data-testid="elec-maint-empty">
          <p>{records.length === 0 ? 'No maintenance records created yet' : 'No records match the current filters'}</p>
          {records.length === 0 && canEditCSIItems && (
            <Button size="sm" className="mt-4" onClick={() => { setEditingRecord(null); setShowModal(true); }} data-testid="elec-maint-empty-create-btn">
              <Plus className="h-4 w-4 mr-1.5" />Add Maintenance
            </Button>
          )}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="elec-maint-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr className="border-b">
                {canManageProject && (
                  <th className="w-10 px-3 py-3">
                    <OrangeCheckbox id="maint-select-all" checked={allSelected} indeterminate={someSelected} onChange={toggleSelectAll} testId="maint-select-all-checkbox" />
                  </th>
                )}
                {config.columns.map(col => (
                  <th key={col.key} className="px-3 py-3 text-left font-semibold text-slate-700">{col.label}</th>
                ))}
                <th className="w-32 px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <tr key={item.id} className={`border-b transition-colors ${isSelected ? 'bg-orange-50/60' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`} data-testid={`maint-row-${item.id}`}>
                    {canManageProject && (
                      <td className="w-10 px-3 py-2"><OrangeCheckbox id={`maint-sel-${item.id}`} checked={isSelected} onChange={() => toggleSelectRow(item.id)} testId={`maint-select-${item.id}`} /></td>
                    )}
                    {config.columns.map(col => (
                      <td key={col.key} className={`px-3 py-2 ${col.cellClass || 'text-slate-700'}`}>
                        {col.render ? col.render(item) : (item[col.key] ?? '-')}
                      </td>
                    ))}
                    <td className="w-32 px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => { setReviewingRecord(item); setShowReview(true); }} data-testid={`maint-review-${item.id}`} title="Review">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => { setEditingRecord(item); setShowModal(true); }} data-testid={`maint-edit-${item.id}`} title="Edit">
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => handleDelete(item)} disabled={deleting === item.id} data-testid={`maint-delete-${item.id}`} title="Delete">
                          {deleting === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      <MaintenanceFormModal
        open={showModal}
        onOpenChange={(open) => { if (!open) { setShowModal(false); setEditingRecord(null); } }}
        editItem={editingRecord}
        onSave={handleSave}
        onRefresh={fetchRecords}
        technicians={technicians}
        projectId={projectId}
        csiItemId={csiItemId}
      />

      {/* Review Modal */}
      <MaintenanceReviewModal
        open={showReview}
        onOpenChange={(open) => { if (!open) { setShowReview(false); setReviewingRecord(null); } }}
        item={reviewingRecord}
      />

      <ConfirmDialog {...confirmState} onClose={() => setConfirmState({ open: false })} />
    </div>
  );
};
